import { useQueries, useQuery } from '@tanstack/react-query'
import { fetchRegionGeometry, regionGeometryParams } from './studio-mutate-v1';

const queryGetAd = (ad, params, options = {}) => {

    return useQuery({
        queryKey: ['api-v1-studio-get-ad', ad, params],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.ads.show', ad), {
                params
            });
            return data || [];
        },
        ...options,
    });

}

const queryGetCampaign = (ad, params, options = {}) => {

    return useQuery({
        queryKey: ['api-v1-studio-get-campaign', ad, params],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.campaign.show', ad), {
                params
            });
            return data || [];
        },
        ...options,
    });

}

/**
 * AD-186: everything the ad detail modal's Performance tab draws, in one call --
 * the delivery tiles, the device split and the top websites.
 */
const queryGetAdPerformance = (ad, options = {}) => {

    return useQuery({
        queryKey: ['api-v1-studio-get-ad-performance', ad],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.ads.performance', ad));
            return data || {};
        },
        ...options,
    });

}

// The endpoint pages by 10 for the CMS table; the studio pickers show every package.
const ALL_PACKAGES_LENGTH = 1000;

const queryGetPackages = (params) => {

    return useQuery({
        queryKey: ['api-v1-studio-get-packages', params],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.packages.fetch-all'), {
                params: { length: ALL_PACKAGES_LENGTH, ...params }
            });
            return data || [];
        },
    });

}

const queryTargetingSearch = (params) => {

    return useQuery({
        queryKey: ['api-v1-studio-targeting-search', params],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.regions.fetch-all'), {
                params
            });
            return data?.data || [];
        },
    });

}


// AD-202: the flat-rate tiers one integration offers, for Quick mode.
const queryGetIntegrationTiers = (newspaperId) => {

    return useQuery({
        queryKey: ['api-v1-studio-get-integration-tiers', newspaperId],
        enabled: Boolean(newspaperId),
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.integrations.tiers.index', newspaperId));
            return data || [];
        },
    });

}

// AD-192: the simplified regions one integration offers, each carrying the targeting
// configured for it.
const queryGetRegionTargeting = (newspaperId) => {

    return useQuery({
        queryKey: ['api-v1-studio-get-integration-regions', newspaperId],
        enabled: Boolean(newspaperId),
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.integrations.region-targeting.index', newspaperId));
            return data?.data || [];
        },
    });

}

// AD-217: the outlines of several regions at once, for the reach preview and the editable map.
const queryGetRegionGeometries = (targets) => {

    return useQueries({
        queries: (targets || []).map((target) => ({
            queryKey: ['api-v1-studio-get-region-geometry', target.full_address, target.targeting_option_id],
            queryFn: async () => await fetchRegionGeometry(
                regionGeometryParams(target.full_address, target.targeting_option_id, target.geo_region_type)
            ),
            staleTime: Infinity,
        })),
    });

}

/**
 * AD-187: the caller's own profile, for Profile Settings.
 */
const queryGetProfile = (options = {}) => {

    return useQuery({
        queryKey: ['api-v1-studio-profile'],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.profile.show'));
            return data || {};
        },
        ...options,
    });

}

/**
 * AD-187: the integrations that sell Ad Studio, as select options. An account can only
 * be linked to one of these, so the invite and edit dropdowns list nothing else.
 */
const queryGetAdStudioIntegrationOptions = (options = {}) => {

    return useQuery({
        queryKey: ['api-v1-studio-adstudio-integration-options'],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.integrations.fetch-all'), {
                params: { start: 0, length: 500 },
            });
            return (data?.data || []).map((integration) => ({
                value: String(integration.id),
                label: integration.name,
            }));
        },
        staleTime: 5 * 60 * 1000,
        ...options,
    });

}

/**
 * AD-187: the invitation behind a link, before the accept form is shown.
 */
const queryGetInvitation = (token, options = {}) => {

    return useQuery({
        queryKey: ['api-v1-studio-invitation', token],
        queryFn: async () => {
            const { data } = await axios.get(route('studio.v1.api.invitation.show', token));
            return data || {};
        },
        retry: false,
        ...options,
    });

}

export { queryGetAd, queryGetAdPerformance, queryGetCampaign, queryGetPackages, queryTargetingSearch, queryGetIntegrationTiers, queryGetRegionTargeting, queryGetRegionGeometries, queryGetProfile, queryGetAdStudioIntegrationOptions, queryGetInvitation }